import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, FileImage, Palette, Share2, FileText, Info } from 'lucide-react';
// Removed Trans import - using direct text instead of translations
import { updateUnivers } from 'store/actions/requests.js';

interface EditUniversModalProps {
  isOpen: boolean;
  onClose: () => void;
  univers: any;
  onUpdate: (updatedUnivers: any) => void;
}

const FieldInfo = ({ title, description }) => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
    <div className="flex items-start gap-2">
      <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
      <div>
        <h4 className="font-medium text-blue-900">{title}</h4>
        <p className="text-sm text-blue-700 mt-1">{description}</p>
      </div>
    </div>
  </div>
);

const EditUniversModal: React.FC<EditUniversModalProps> = ({
  isOpen,
  onClose,
  univers,
  onUpdate
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    background_image: null,
    preview_image: null,
    course_pdf: null,
    color_scheme: {},
    is_shared: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (univers) {
      setFormData({
        name: univers.name || '',
        description: univers.description || '',
        background_image: univers.background_image || null,
        preview_image: univers.preview_image || null,
        course_pdf: univers.course_pdf || null,
        color_scheme: univers.color_scheme || {},
        is_shared: univers.is_shared || false
      });
    }
  }, [univers]);

  const handleFileChange = (field: string, file: File | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const handleColorSchemeChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      color_scheme: {
        ...prev.color_scheme,
        [key]: value
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      
      // Ajouter les champs texte
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('is_shared', formData.is_shared.toString());
      formDataToSend.append('color_scheme', JSON.stringify(formData.color_scheme));

      // Ajouter les fichiers s'ils ont été modifiés
      if (formData.background_image instanceof File) {
        formDataToSend.append('background_image', formData.background_image);
      }
      if (formData.preview_image instanceof File) {
        formDataToSend.append('preview_image', formData.preview_image);
      }
      if (formData.course_pdf instanceof File) {
        formDataToSend.append('course_pdf', formData.course_pdf);
      }

      const response = await updateUnivers(univers.id, formDataToSend);
      onUpdate(response.data);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Une erreur est survenue lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
Modifier le cours : {univers?.name}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations de base */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
Informations de base
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FieldInfo 
                title="Nom et description"
                description="Le nom apparaît dans la liste des cours. La description est utilisée pour présenter le contenu du cours aux apprenants."
              />
              
              <div className="space-y-2">
                <Label htmlFor="name">Nom du cours</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  rows={4}
                  placeholder="Description du cours..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Images */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileImage className="h-5 w-5" />
Images du cours
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FieldInfo 
                title="Images visuelles"
                description="L'image de prévisualisation apparaît dans la liste des cours. L'image d'arrière-plan est utilisée comme fond sur la page du cours."
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Image de prévisualisation</Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange('preview_image', e.target.files?.[0] || null)}
                      className="w-full"
                    />
                    {formData.preview_image && (
                      <p className="text-sm text-green-600 mt-2">
                        {formData.preview_image instanceof File ? formData.preview_image.name : 'Image actuelle'}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Image d'arrière-plan</Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange('background_image', e.target.files?.[0] || null)}
                      className="w-full"
                    />
                    {formData.background_image && (
                      <p className="text-sm text-green-600 mt-2">
                        {formData.background_image instanceof File ? formData.background_image.name : 'Image actuelle'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PDF du cours */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
Contenu pédagogique
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FieldInfo 
                title="PDF du cours"
                description="Ce document PDF sera affiché dans un panneau latéral quand les apprenants cliqueront sur 'En savoir plus'. Il peut contenir le programme détaillé, les objectifs pédagogiques, etc."
              />

              <div className="space-y-2">
                <Label>PDF du cours</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileChange('course_pdf', e.target.files?.[0] || null)}
                    className="w-full"
                  />
                  {formData.course_pdf && (
                    <p className="text-sm text-green-600 mt-2">
                      {formData.course_pdf instanceof File ? formData.course_pdf.name : 'PDF actuel'}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Couleurs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
Thème et couleurs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FieldInfo 
                title="Personnalisation visuelle"
                description="Définissez les couleurs principales et secondaires qui seront utilisées dans l'interface du cours pour créer une identité visuelle cohérente."
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Couleur principale</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.color_scheme.primary || '#3b82f6'}
                      onChange={(e) => handleColorSchemeChange('primary', e.target.value)}
                      className="w-12 h-10 rounded border"
                    />
                    <Input
                      value={formData.color_scheme.primary || '#3b82f6'}
                      onChange={(e) => handleColorSchemeChange('primary', e.target.value)}
                      placeholder="#3b82f6"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Couleur secondaire</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.color_scheme.secondary || '#6b7280'}
                      onChange={(e) => handleColorSchemeChange('secondary', e.target.value)}
                      className="w-12 h-10 rounded border"
                    />
                    <Input
                      value={formData.color_scheme.secondary || '#6b7280'}
                      onChange={(e) => handleColorSchemeChange('secondary', e.target.value)}
                      placeholder="#6b7280"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Partage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5" />
Paramètres de partage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FieldInfo 
                title="Cours partagé"
                description="Un cours partagé peut être utilisé par d'autres centres de formation. Cela permet la collaboration et le partage de contenus pédagogiques entre établissements."
              />

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_shared"
                  checked={formData.is_shared}
                  onCheckedChange={(checked) => setFormData(prev => ({...prev, is_shared: checked as boolean}))}
                />
                <Label htmlFor="is_shared" className="text-sm font-medium">
Partager ce cours avec d'autres centres de formation
                </Label>
              </div>
            </CardContent>
          </Card>

          <Separator />

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUniversModal;